import type { AudienceMegaMenuHeaderRuntimePayload } from "@multi-tenant-cms/types/menu";

/** localStorage key and first-party cookie name — keep in sync for SSR + client. */
export const AUDIENCE_PREFERENCE_COOKIE = "mmh-audience";

const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365; // 1 year

function pathMatchesHub(pathname: string, hubUrl: string): boolean {
	const hub = hubUrl.trim();
	if (!hub || hub === "#") {
		return false;
	}
	try {
		if (hub.startsWith("http://") || hub.startsWith("https://")) {
			const u = new URL(hub);
			return pathname === u.pathname || pathname.startsWith(`${u.pathname}/`);
		}
	} catch {
		return false;
	}
	const path = hub.startsWith("/") ? hub : `/${hub}`;
	return pathname === path || pathname.startsWith(`${path}/`);
}

/** Prefer hub URL match for the current pathname (SSR-safe). */
export function audienceIdFromPath(
	payload: AudienceMegaMenuHeaderRuntimePayload,
	pathname: string | null | undefined
): string | null {
	if (!pathname) {
		return null;
	}
	for (const slot of payload.audiences) {
		if (pathMatchesHub(pathname, slot.hubUrl)) {
			return slot.id;
		}
	}
	return null;
}

/** Configured default when path / cookie do not dictate an audience. */
export function defaultAudienceId(
	payload: AudienceMegaMenuHeaderRuntimePayload
): string {
	const validIds = new Set(payload.audiences.map((a) => a.id));
	if (validIds.has(payload.defaultAudienceId)) {
		return payload.defaultAudienceId;
	}
	return payload.audiences[0].id;
}

function validAudienceIds(
	payload: AudienceMegaMenuHeaderRuntimePayload
): Set<string> {
	return new Set(payload.audiences.map((a) => a.id));
}

/**
 * Cookie preference when persist is on and the value matches a configured audience.
 * Used by Region SSR (`cookies().get`) and unit tests.
 */
export function audienceIdFromCookie(
	payload: AudienceMegaMenuHeaderRuntimePayload,
	cookieValue: string | null | undefined
): string | null {
	if (!payload.persistPreference) {
		return null;
	}
	if (!cookieValue) {
		return null;
	}
	const id = cookieValue.trim();
	if (!(id && validAudienceIds(payload).has(id))) {
		return null;
	}
	return id;
}

/**
 * SSR / first-paint audience: path hub → cookie preference → configured default.
 * localStorage is client-only and must not drive the initial React tree.
 */
export function initialAudienceId(
	payload: AudienceMegaMenuHeaderRuntimePayload,
	pathname?: string | null,
	preferredAudienceId?: string | null
): string {
	return (
		audienceIdFromPath(payload, pathname) ??
		audienceIdFromCookie(payload, preferredAudienceId) ??
		defaultAudienceId(payload)
	);
}

/** Client-only: mirror preference into a request cookie for the next SSR. */
export function writeAudiencePreferenceCookie(audienceId: string): void {
	try {
		const secure =
			typeof window !== "undefined" && window.location.protocol === "https:"
				? "; Secure"
				: "";
		// biome-ignore lint/suspicious/noDocumentCookie: Audience preference must be readable on the next SSR request to avoid FOUC.
		document.cookie = `${AUDIENCE_PREFERENCE_COOKIE}=${encodeURIComponent(audienceId)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`;
	} catch {
		// ignore private mode / blocked cookies
	}
}
